import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  CssBaseline, 
  Tab, 
  Tabs, 
  ThemeProvider, 
  createTheme,
  Typography,
  AppBar,
  Toolbar,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Link,
  Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import ProductInputForm from './components/ProductInputForm.tsx';
import ResultsDisplay from './components/ResultsDisplay.tsx';
import BoxManagement from './components/BoxManagement.tsx';
import NoBoxesAlert from './components/NoBoxesAlert.tsx';
import DemoBoxesBanner from './components/DemoBoxesBanner.tsx';
import SharedResults from './components/SharedResults.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import StatusRail from './components/StatusRail.tsx';
import FillCube from './components/FillCube.tsx';
import { findBestPackagingSolutions, findMultiProductPackagingSolutions } from './services/packagingCalculator.ts';
import { getAllBoxes, getVisibleBoxes, reconcileExampleBoxes } from './services/database.ts';
import { getMarginInches, getPreferredUnit } from './services/preferences.ts';
import { MeasurementUnit } from './types/index.ts';
import { MultiProductRequest, PackagingSolution, Product, BRAND_COLORS, FONTS } from './types/index.ts';

// Load Deck typefaces (self-hosted via @fontsource)
import '@fontsource/saira-semi-condensed/500.css';
import '@fontsource/saira-semi-condensed/600.css';
import '@fontsource/saira-semi-condensed/700.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';

// Fonction pour vérifier si nous sommes sur la page de partage.
// Match strict (chemin /shared ou paramètre ?shared=) : un simple substring
// sur l'URL complète détournait toute page dont l'URL contenait « /shared ».
const isSharedResultsUrl = () => {
  const { pathname, search } = window.location;
  return (
    pathname === '/shared' ||
    pathname.startsWith('/shared/') ||
    new URLSearchParams(search).has('shared')
  );
};

// Load Deck theme — shipping-operations console.
const theme = createTheme({
  palette: {
    primary: { main: BRAND_COLORS.dockBlue },
    secondary: { main: BRAND_COLORS.green },
    background: { default: BRAND_COLORS.deck, paper: BRAND_COLORS.cardStock },
    text: { primary: BRAND_COLORS.ink },
    divider: 'rgba(23, 34, 46, 0.16)',
  },
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: FONTS.body,
    h1: { fontFamily: FONTS.display, fontWeight: 700 },
    h2: { fontFamily: FONTS.display, fontWeight: 700 },
    h3: { fontFamily: FONTS.display, fontWeight: 700 },
    h4: { fontFamily: FONTS.display, fontWeight: 700, letterSpacing: '0.02em' },
    h5: { fontFamily: FONTS.display, fontWeight: 700, letterSpacing: '0.03em' },
    h6: { fontFamily: FONTS.display, fontWeight: 600, letterSpacing: '0.02em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    overline: { fontFamily: FONTS.display, fontWeight: 600, letterSpacing: '0.1em' },
    button: { fontFamily: FONTS.display, fontWeight: 600, letterSpacing: '0.06em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.06em',
          transition: 'transform 60ms ease, box-shadow 120ms ease, background-color 120ms ease',
          // "stamp press" — buttons feel like stamping a label
          '&:active': { transform: 'translateY(1px)' },
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: BRAND_COLORS.kraft, height: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONTS.display,
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.08em',
          minHeight: 56,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1.5px solid ${BRAND_COLORS.ink}`,
          backgroundColor: BRAND_COLORS.cardStock,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: FONTS.display,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND_COLORS.dockBlue,
            borderWidth: 2,
          },
        },
      },
    },
  },
});

// Interface for tab panel properties
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  // Keep the panel mounted (just hidden) when inactive so its form state
  // survives tab switches instead of being reset on remount.
  keepMounted?: boolean;
}

// Component for each tab's content
function TabPanel(props: TabPanelProps) {
  const { children, value, index, keepMounted, ...other } = props;
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ padding: '24px 0' }}
    >
      {(isActive || keepMounted) && children}
    </div>
  );
}

// Numbered "station" label for the console tabs (mono index + Saira caps).
const StationLabel: React.FC<{ n: string; text: string }> = ({ n, text }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box component="span" sx={{ fontFamily: FONTS.mono, fontSize: 11, opacity: 0.65 }}>
      {n}
    </Box>
    {text}
  </Box>
);

// Composant pour l'application principale (sans les résultats partagés)
const MainAppContent: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [solutions, setSolutions] = useState<PackagingSolution[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hasBoxes, setHasBoxes] = useState(false);
  const [boxCount, setBoxCount] = useState(0);
  const [visibleBoxCount, setVisibleBoxCount] = useState(0);
  // Vrai quand la base ne contient QUE des boîtes d'exemple (mode démo).
  const [showDemoBanner, setShowDemoBanner] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [noSolutionsAlertOpen, setNoSolutionsAlertOpen] = useState(false);
  // True when the last empty search was caused by every box being hidden.
  const [noSolutionsAllHidden, setNoSolutionsAllHidden] = useState(false);
  // Preferences snapshot for the status rail — refreshed by children through
  // onPreferencesChanged, so the rail never shows a stale unit/margin.
  const [prefs, setPrefs] = useState<{ unit: MeasurementUnit; marginInches: number }>(() => ({
    unit: getPreferredUnit(),
    marginInches: getMarginInches(),
  }));
  const refreshPrefs = () => setPrefs({ unit: getPreferredUnit(), marginInches: getMarginInches() });
  // Snapshot of the inputs the current solutions were computed with, so stale
  // results (boxes or margin changed afterwards) can be flagged.
  const [calcSnapshot, setCalcSnapshot] = useState<{ margin: number; boxesJson: string } | null>(null);

  // Check if boxes exist when the app loads
  useEffect(() => {
    checkBoxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to check if boxes exist
  const checkBoxes = () => {
    // Réconcilie le mode démo AVANT de lire : seed les exemples si la base est
    // vide, ou les retire dès qu'une vraie boîte existe.
    reconcileExampleBoxes();
    const boxes = getAllBoxes();
    setHasBoxes(boxes.length > 0);
    setBoxCount(boxes.length);
    setVisibleBoxCount(getVisibleBoxes().length);
    setShowDemoBanner(boxes.length > 0 && boxes.every((box) => box.isExample === true));
  };

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Calculate best packaging solutions
  const handleProductSubmit = (submittedProducts: Product[]) => {
    setIsCalculating(true);
    setProducts(submittedProducts);

    // setTimeout(0): laisse le spinner s'afficher avant le calcul synchrone.
    setTimeout(() => {
      if (submittedProducts.length > 0) {
        const boxes = getVisibleBoxes(); // Use only visible boxes
        const margin = getMarginInches(); // User-configured safety margin (inches)

        // If there's more than one product, use multi-product calculation
        let calculatedSolutions: PackagingSolution[];

        if (submittedProducts.length > 1) {
          const multiProductRequest: MultiProductRequest = {
            products: submittedProducts
          };
          calculatedSolutions = findMultiProductPackagingSolutions(multiProductRequest, boxes, margin);
        } else {
          // Single product case (existing logic)
          const firstProduct = submittedProducts[0];
          calculatedSolutions = findBestPackagingSolutions(firstProduct, boxes, margin);
        }

        setSolutions(calculatedSolutions);
        setCalcSnapshot({ margin, boxesJson: JSON.stringify(getAllBoxes()) });

        // Switch to results tab if solutions found
        if (calculatedSolutions.length > 0) {
          setTabValue(1);
        } else {
          // Aucune solution : ne jamais laisser l'onglet Results (désormais
          // désactivé) sélectionné et vide, et expliquer la vraie cause si
          // toutes les caisses sont masquées.
          setNoSolutionsAllHidden(getAllBoxes().length > 0 && boxes.length === 0);
          setNoSolutionsAlertOpen(true);
          setTabValue((current) => (current === 1 ? 0 : current));
        }
      }
      setIsCalculating(false);
    }, 0);
  };

  // Fermer le Dialog d'alerte
  const handleCloseNoSolutionsAlert = () => {
    setNoSolutionsAlertOpen(false);
  };

  // Status-rail readouts (refresh on tab / box / preference changes).
  const resultsReady = solutions.length > 0;
  const bestUtil = resultsReady ? solutions[0]?.spaceUtilization ?? 0 : 0;
  const displayUnit = prefs.unit;
  const marginDisplay = displayUnit === 'cm' ? prefs.marginInches * 2.54 : prefs.marginInches;
  const statusItems = [
    { k: 'UNIT', v: displayUnit },
    { k: 'MARGIN', v: `${marginDisplay.toFixed(2)} ${displayUnit}` },
    {
      k: 'BOXES',
      v: visibleBoxCount === boxCount ? String(boxCount) : `${visibleBoxCount}/${boxCount} visible`,
    },
    ...(resultsReady ? [{ k: 'SOLUTIONS', v: String(solutions.length) }] : []),
  ];

  // Les solutions affichées sont-elles encore valables ? (caisses ou marge
  // modifiées depuis le calcul → l'utilisateur doit relancer la recherche)
  const resultsStale =
    resultsReady &&
    calcSnapshot !== null &&
    (calcSnapshot.margin !== prefs.marginInches ||
      calcSnapshot.boxesJson !== JSON.stringify(getAllBoxes()));

  return (
    <>
      {/* Console header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: BRAND_COLORS.dockBlue,
          color: '#fff',
          borderBottom: `2px solid ${BRAND_COLORS.ink}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, md: 64 } }}>
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1.5, color: '#fff' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1.5 }}>
            <img src="/boxfinder-icon.svg" alt="Box Finder logo" style={{ height: 34, width: 34 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: '#fff',
                fontFamily: FONTS.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Box Finder
            </Typography>
          </Box>

          {!isMobile && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiTab-root.Mui-selected': { color: '#fff' },
                '& .MuiTab-root.Mui-disabled': { color: 'rgba(255,255,255,0.38)' },
              }}
            >
              {/* id/aria-controls : les tabpanels référencent ces ids via
                  aria-labelledby — sans eux, la liaison ARIA était cassée. */}
              <Tab
                id="simple-tab-0"
                aria-controls="simple-tabpanel-0"
                label={<StationLabel n="01" text="Search" />}
              />
              <Tab
                id="simple-tab-1"
                aria-controls="simple-tabpanel-1"
                iconPosition="start"
                icon={<FillCube utilization={resultsReady ? bestUtil : 0} size={18} animate={false} onDark />}
                label={<StationLabel n="02" text="Results" />}
                disabled={solutions.length === 0}
              />
              <Tab
                id="simple-tab-2"
                aria-controls="simple-tabpanel-2"
                label={<StationLabel n="03" text="Box Management" />}
              />
            </Tabs>
          )}
        </Toolbar>
      </AppBar>

      {/* Always-on status rail — the "instrument, not admin panel" tell */}
      <StatusRail items={statusItems} trailing={new Date().toISOString().slice(0, 10)} />


      {/* Navigation drawer for mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 250 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.primary }}>
              Menu
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            <ListItem 
              button 
              selected={tabValue === 0}
              onClick={() => {
                setTabValue(0);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                <SearchIcon color={tabValue === 0 ? 'secondary' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary="Search" />
            </ListItem>
            <ListItem 
              button 
              selected={tabValue === 1}
              disabled={solutions.length === 0}
              onClick={() => {
                setTabValue(1);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                <InventoryIcon color={tabValue === 1 ? 'secondary' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary="Results" />
            </ListItem>
            <ListItem 
              button 
              selected={tabValue === 2}
              onClick={() => {
                setTabValue(2);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                <InfoIcon color={tabValue === 2 ? 'secondary' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary="Box Management" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Display alert if no boxes exist */}
        {!hasBoxes && tabValue !== 2 && (
          <NoBoxesAlert
            onGoToBoxManagement={() => setTabValue(2)}
          />
        )}

        {/* Mode démo : bannière sur l'écran d'accueil (onglet Recherche) tant que
            seules les boîtes d'exemple sont présentes. Mutuellement exclusive avec
            NoBoxesAlert (les exemples comptent comme des boîtes → hasBoxes vrai). */}
        {showDemoBanner && tabValue === 0 && <DemoBoxesBanner />}
        
        {/* Main content */}
        {/* keepMounted: the Search form keeps everything the user entered
            (products, quantities, dimensions, rotation options, units) when
            navigating to Results / Box Management and back. */}
        <TabPanel value={tabValue} index={0} keepMounted>
          <ProductInputForm
            onProductSubmit={handleProductSubmit}
            isLoading={isCalculating}
            onPreferencesChanged={refreshPrefs}
          />
        </TabPanel>

        {/* keepMounted : les sélections de partage, prix et filtres de l'onglet
            Results survivent aux allers-retours vers les autres onglets. */}
        <TabPanel value={tabValue} index={1} keepMounted>
          {resultsStale && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your boxes or safety margin changed after these results were computed — run the
              search again to refresh them.
            </Alert>
          )}
          {solutions.length > 0 && (
            <ResultsDisplay
              product={products.length > 1 ? products : products[0]}
              solutions={solutions}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <BoxManagement onBoxesChanged={checkBoxes} onPreferencesChanged={refreshPrefs} />
        </TabPanel>
      </Container>

      {/* Footer — thin manifest strip */}
      <Box
        component="footer"
        sx={{
          py: 1.5,
          px: 2,
          mt: 'auto',
          backgroundColor: BRAND_COLORS.dockBlue,
          color: 'rgba(255,255,255,0.75)',
          borderTop: `2px solid ${BRAND_COLORS.ink}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            align="center"
            sx={{ fontFamily: FONTS.mono, fontSize: 12, letterSpacing: '0.02em' }}
          >
            © {new Date().getFullYear()} · Box Finder · a homemade tool by{' '}
            <Link
              href="https://killian.borowiec.ch"
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ color: 'inherit' }}
            >
              Killian Borowiec
            </Link>
          </Typography>
        </Container>
      </Box>

      {/* Dialog d'alerte quand aucune solution n'est trouvée */}
      <Dialog
        open={noSolutionsAlertOpen}
        onClose={handleCloseNoSolutionsAlert}
        aria-labelledby="no-solutions-dialog-title"
        aria-describedby="no-solutions-dialog-description"
      >
        <DialogTitle id="no-solutions-dialog-title" sx={{ color: BRAND_COLORS.primary }}>
          No Solutions Found
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="no-solutions-dialog-description">
            {noSolutionsAllHidden ? (
              <>
                All your boxes are currently hidden, so none can be used in the calculation.
                Go to Box Management and make at least one box visible (the eye icon), then run
                the search again.
              </>
            ) : (
              <>
                No solutions were found for the specified product(s). Please check:
                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                  <li>The dimensions of your products</li>
                  <li>The selected rotation options</li>
                  <li>The availability of large enough boxes</li>
                </Box>
                Try adding more rotation options or using larger boxes.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoSolutionsAlert} variant="contained" color="primary" autoFocus>
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Composant principal de l'application
function App() {
  // On utilise le composant adéquat en fonction de l'URL ; la frontière
  // d'erreur évite la page blanche si un rendu jette (WebGL absent, etc.).
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        {isSharedResultsUrl() ? <SharedResults /> : <MainAppContent />}
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App; 